import React, { useState } from 'react';
import { X, Calendar, Clock, User, MessageSquare } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { useI18n } from '@/contexts/I18nContext';
import { Slot } from '@/types/classRequests';
import { FramedAvatar } from '@/components/ui/FramedAvatar';

interface ClassRequestModalProps {
  isOpen: boolean;
  onClose: () => void;
  slot: Slot;
  instructorId: string;
  instructorName: string;
  instructorAvatar?: string;
  instructorBeltRank?: string;
  selectedDate: Date;
  onSubmit: (message?: string) => Promise<void>;
  isLoading?: boolean;
}

export const ClassRequestModal: React.FC<ClassRequestModalProps> = ({
  isOpen,
  onClose,
  slot,
  instructorId,
  instructorName,
  instructorAvatar,
  instructorBeltRank,
  selectedDate,
  onSubmit,
  isLoading = false
}) => {
  const { t } = useI18n();
  const [message, setMessage] = useState('');

  if (!isOpen) return null;

  const formatTime = (timeString: string) => {
    // Handle different time formats
    let time = timeString;
    if (timeString.includes('T')) {
      time = timeString.split('T')[1]?.split('.')[0] || timeString;
    }
    
    const [hours, minutes] = time.split(':').map(Number);
    
    // Convert to 12-hour format
    const ampm = hours >= 12 ? 'PM' : 'AM';
    const displayHour = hours === 0 ? 12 : hours > 12 ? hours - 12 : hours;
    return `${displayHour}:${minutes.toString().padStart(2, '0')} ${ampm}`;
  };

  const formatDate = (date: Date) => {
    try {
      return date.toLocaleDateString('en-US', { 
        month: 'long', 
        day: 'numeric', 
        year: 'numeric' 
      });
    } catch (error) {
      console.error('Error formatting date:', date, error);
      return 'Invalid Date';
    }
  };

  const handleSubmit = async () => {
    await onSubmit(message.trim() || undefined);
    setMessage('');
  };

  const handleClose = () => {
    setMessage('');
    onClose();
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between p-6 border-b border-gray-200">
            <h2 className="text-lg font-bold text-black">
              Request Private Class
            </h2>
          <button
            onClick={handleClose}
            className="p-2 text-gray-400 hover:text-gray-600 transition-colors rounded-lg hover:bg-gray-100"
            disabled={isLoading}
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Class Title */}
          <div>
            <h3 className="text-lg font-bold text-black mb-2">
              Private Class - Gi
            </h3>
            <p className="text-sm text-gray-500">
              {formatDate(selectedDate)} - {formatTime(slot.start_time)} to {formatTime(slot.end_time)}
            </p>
          </div>

          {/* Instructor Info */}
          <div>
            <p className="text-sm text-gray-500 mb-2">Instructor</p>
            <div className="flex items-center gap-3">
              <FramedAvatar
                src={instructorAvatar}
                alt={instructorName}
                size={32}
                showFrame={true}
                beltRank={instructorBeltRank}
                frameSize={{ w: 40, h: 44 }}
              />
              <p className="text-sm text-gray-500">{instructorName}</p>
            </div>
          </div>

          {/* Duration */}
          <div>
            <p className="text-sm text-gray-500 mb-1">Duration</p>
            <p className="text-sm text-gray-500">
              {slot.duration_minutes} minutes
            </p>
          </div>

          {/* Message Field */}
          <div>
            <p className="text-sm text-gray-500 mb-2">Message (Optional)</p>
            <Textarea
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Add a message for the instructor..."
              className="bg-gray-50 border-gray-300 text-black placeholder-gray-400 focus:border-gray-400 focus:ring-gray-400 rounded-lg"
              rows={3}
              disabled={isLoading}
            />
          </div>

          {/* Price Info */}
          <div>
            <p className="text-sm text-gray-500 mb-1">Price</p>
            <p className="text-sm text-gray-500">
              $60/hour - Payment will be handled directly with the instructor
            </p>
          </div>
        </div>

        {/* Footer */}
        <div className="flex gap-3 p-6 border-t border-gray-200">
          <Button
            variant="outline"
            onClick={handleClose}
            className="flex-1 border-gray-300 text-black hover:bg-gray-50 rounded-full"
            disabled={isLoading}
          >
            Cancel
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={isLoading}
            className="flex-1 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-full"
          >
            {isLoading ? (
              <div className="flex items-center gap-2">
                <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                Submitting...
              </div>
            ) : (
              'Submit Request'
            )}
          </Button>
        </div>
      </div>
    </div>
  );
};
